import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Typography, Link, Card, CardContent, Chip } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { sectionBackgrounds, resolveGradient } from '../../../theme';
const DEFAULT_COUNT_LABEL = 'applications available';
const DEFAULT_EMPTY_MESSAGE = 'No agencies to display.';
const DEFAULT_LINK_TEXT = 'Browse';
/**
 * AgencyList – list of agencies with optional count and link.
 * Component-level wording: countLabel, emptyMessage, linkText (all overridable via props).
 */
const AgencyList = ({ label, gradient, heading, items = [], countLabel = DEFAULT_COUNT_LABEL, emptyMessage = DEFAULT_EMPTY_MESSAGE, linkText = DEFAULT_LINK_TEXT, paddingTop, paddingBottom, marginTop, marginBottom, maxWidth, fontFamily, fontWeight = 600, componentAlignment = 'left', }) => {
    const background = gradient
        ? resolveGradient(gradient, sectionBackgrounds.hero)
        : 'transparent';
    const countWording = countLabel.trim().toLowerCase();
    const countSuffix = countWording ? ` ${countWording}` : '';
    return (_jsx(Box, { component: "section", sx: {
            background,
            paddingTop: paddingTop || 4,
            paddingBottom: paddingBottom || 4,
            marginTop: marginTop || 0,
            marginBottom: marginBottom || 0,
            textAlign: componentAlignment,
        }, children: _jsxs(Box, { sx: { maxWidth: maxWidth || 900, mx: 'auto', px: 2 }, children: [label && (_jsx(Typography, { variant: "overline", sx: { display: 'block', mb: 2, color: 'text.secondary', letterSpacing: 1 }, children: label })), heading && (_jsx(Typography, { variant: "h5", sx: {
                        mb: 3,
                        fontFamily: fontFamily || undefined,
                        fontWeight: Number(fontWeight) || 700,
                    }, children: heading })), items.length === 0 ? (_jsx(Typography, { variant: "body2", color: "text.secondary", children: emptyMessage })) : (_jsx(Box, { component: "ul", sx: {
                        display: 'grid',
                        gap: 1.5,
                        listStyle: 'none',
                        m: 0,
                        p: 0,
                    }, children: items.map((item, index) => {
                        const hasLink = !!item.href;
                        const countDisplay = item.count != null && item.count !== ''
                            ? `${item.count}${countSuffix}`
                            : null;
                        const content = (_jsx(CardContent, { sx: { py: 2, px: 2.5, '&:last-child': { pb: 2 } }, children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }, children: [_jsxs(Box, { sx: { flex: '1 1 auto', minWidth: 0 }, children: [_jsx(Typography, { component: "span", sx: {
                                                    fontWeight: Number(fontWeight) || 600,
                                                    fontFamily: fontFamily || undefined,
                                                    fontSize: '1rem',
                                                }, children: item.name }), item.description && (_jsx(Typography, { variant: "body2", color: "text.secondary", sx: { display: 'block', mt: 0.25 }, children: item.description }))] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }, children: [countDisplay && (_jsx(Chip, { label: countDisplay, size: "small", variant: "outlined", sx: {
                                                    fontWeight: 500,
                                                    fontSize: '0.75rem',
                                                    borderColor: 'divider',
                                                    color: 'text.secondary',
                                                } })), hasLink && (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }, children: [_jsx(Typography, { variant: "body2", fontWeight: 600, children: linkText }), _jsx(ChevronRightIcon, { sx: { fontSize: 20 } })] }))] })] }) }));
                        return (_jsx(Box, { component: "li", children: hasLink ? (_jsx(Card, { component: Link, href: item.href, underline: "none", sx: {
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    color: 'text.primary',
                                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                    },
                                }, children: content })) : (_jsx(Card, { variant: "outlined", sx: {
                                    borderRadius: 2,
                                    borderColor: 'divider',
                                    bgcolor: 'action.hover',
                                }, children: content })) }, index));
                    }) }))] }) }));
};
export default AgencyList;
